import type {
  NavigationGuardReturn,
  RouteLocationNormalizedGeneric,
} from 'vue-router'

import { getAuthorizer } from '@/auth/useAuthorizer'
import { type useAuthStore } from '@/auth/useAuthStore'
import { checkACL } from '@/utils/acl'

/**
 * New permission checking guard using @owner/auth Authorizer
 * Assumes Authorizer is initialized - caller should check first
 * @param to
 * @param authStore
 */
export const checkPermissionsV2 = (
  to: RouteLocationNormalizedGeneric,
  authStore: ReturnType<typeof useAuthStore>
): NavigationGuardReturn => {
  const authorizer = getAuthorizer()

  if (!authorizer) {
    throw new Error('checkPermissionsV2 called without initialized authorizer')
  }

  // Role check
  if (to.meta.roles) {
    const roles = Array.isArray(to.meta.roles) ? to.meta.roles : [to.meta.roles]

    if (!authorizer.hasRoles(roles, { evaluation: 'some' })) {
      return authStore.home
    }
  }

  // V2 has two permission shapes during the rollout:
  // - `authZ`: new predicate that mirrors the backend `@AuthZ({ user })`
  //   decorator. Preferred whenever a route declares it.
  // - `permissions`: legacy `{ operation, resource }[]` array, evaluated with
  //   `some` semantics. Fallback for routes that haven't migrated yet.
  // V2 always evaluates the most expressive check available — the
  // `hermes.rbac.routerEnforcement` flag in `checkPermissions.ts` decides
  // whether V2's result is authoritative, but V2 itself runs unconditionally
  // so pre-flip discrepancy telemetry exercises the new authZ predicates
  // against V1.
  if (to.meta.authZ) {
    if (!to.meta.authZ({ user: authorizer })) {
      return authStore.home
    }
  } else if (to.meta.permissions) {
    const permissions = Array.isArray(to.meta.permissions)
      ? to.meta.permissions
      : [to.meta.permissions]

    const hasPermissionAccess = permissions.some((perm) =>
      authorizer.can(perm.operation, perm.resource)
    )

    if (!hasPermissionAccess) {
      return authStore.home
    }
  }

  // ACL check (unchanged - still separate concern)
  if (to.meta.acl) {
    const userAcl = authStore.acl
    const aclParams = Object.entries(to.meta.acl).reduce(
      (acc, [collection, routeParam]) =>
        Object.assign(acc, { [collection]: to.params[routeParam] }),
      {}
    )

    if (!checkACL(userAcl, aclParams)) {
      return authStore.home
    }
  }
}
